/**
 * Upload Math Expressions Component
 * Handles JSON file upload for mathematical expressions to update existing records
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error,
  ExpandMore,
  ExpandLess,
  Save,
  Functions,
} from '@mui/icons-material';
import { useAppStore } from '../../store/appStore';
import { predictionsApi } from '../../services/predictions.service';

interface MathExpressionFileData {
  pdf_name: string;
  content_id: string;
  processing_date?: string;
  expressions: any[];
}

interface ValidationError {
  file: string;
  errors: string[];
}

interface UploadResult {
  id: string;
  success: boolean;
  message: string;
  details?: any;
}

export default function UploadMathExpressions() {
  const { selectedProduct, selectedExperiment } = useAppStore();
  const [files, setFiles] = useState<File[]>([]);
  const [fileData, setFileData] = useState<Map<string, MathExpressionFileData>>(new Map());
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [overwriteEnabled, setOverwriteEnabled] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [existingRecords, setExistingRecords] = useState<string[]>([]);
  const [expandedErrors, setExpandedErrors] = useState(false);

  const handleFilesUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(event.target.files || []);
    setFiles(uploadedFiles);

    // Reset state
    setFileData(new Map());
    setValidationErrors([]);
    setUploadResults([]);
    setExistingRecords([]);

    await processFiles(uploadedFiles);
  };

  const processFiles = async (uploadedFiles: File[]) => {
    setIsProcessing(true);
    const newFileData = new Map<string, MathExpressionFileData>();
    const errors: ValidationError[] = [];

    for (const file of uploadedFiles) {
      try {
        const text = await file.text();
        const data = JSON.parse(text);

        // Validate JSON structure
        const fileErrors = validateJsonStructure(data);

        if (fileErrors.length > 0) {
          errors.push({ file: file.name, errors: fileErrors });
        } else {
          newFileData.set(data.content_id, data);
        }
      } catch (error: any) {
        errors.push({
          file: file.name,
          errors: [`Invalid JSON: ${error?.message || 'Unknown error'}`]
        });
      }
    }

    setFileData(newFileData);
    setValidationErrors(errors);

    // Check for existing records if we have valid data
    if (newFileData.size > 0) {
      await checkExistingRecords();
    }

    setIsProcessing(false);
  };

  const validateJsonStructure = (data: any): string[] => {
    const errors: string[] = [];

    // Check required fields
    if (!data.content_id || typeof data.content_id !== 'string') {
      errors.push('Missing or invalid content_id');
    }

    if (!data.pdf_name || typeof data.pdf_name !== 'string') {
      errors.push('Missing or invalid pdf_name');
    }

    if (!data.expressions || !Array.isArray(data.expressions)) {
      errors.push('Missing or invalid expressions array');
    } else {
      // Validate expressions structure
      data.expressions.forEach((expr: any, index: number) => {
        if (!expr.contentExpression) {
          errors.push(`expressions[${index}]: missing contentExpression`);
        } else {
          const content = expr.contentExpression;
          if (!content.id || typeof content.id !== 'string') {
            errors.push(`expressions[${index}].contentExpression: missing or invalid id`);
          }
          if (!content.latex || typeof content.latex !== 'string') {
            errors.push(`expressions[${index}].contentExpression: missing or invalid latex`);
          }
          if (typeof content.selected !== 'boolean') {
            errors.push(`expressions[${index}].contentExpression: missing or invalid selected (must be boolean)`);
          }
        }

        if (expr.decomposed && !Array.isArray(expr.decomposed)) {
          errors.push(`expressions[${index}]: decomposed must be an array if present`);
        }
      });
    }

    return errors;
  };

  const checkExistingRecords = async () => {
    if (!selectedProduct || !selectedExperiment) {
      return;
    }

    try {
      // For now, we'll simulate checking existing records
      // In a real implementation, this would make an API call
      const existingIds: string[] = [];
      setExistingRecords(existingIds);
    } catch (error: any) {
      console.error('Error checking existing records:', error);
    }
  };

  const uploadMathExpressions = async () => {
    if (!selectedProduct || !selectedExperiment) {
      return;
    }

    setIsUploading(true);
    const results: UploadResult[] = [];

    for (const [contentId, data] of fileData) {
      try {
        // Upload both original and modified with the same content (following tag predictions pattern)
        const response = await predictionsApi.update(selectedProduct!, selectedExperiment!, contentId, {
          mathematical_expressions_original: data,
          mathematical_expressions_modified: data,
        } as any);

        results.push({
          id: contentId,
          success: true,
          message: `Successfully uploaded ${data.expressions.length} expressions`,
          details: response
        });
      } catch (error: any) {
        results.push({
          id: contentId,
          success: false,
          message: error?.message || 'Update failed'
        });
      }
    }

    setUploadResults(results);
    setIsUploading(false);
  };

  const validFiles = fileData.size;
  const invalidFiles = validationErrors.length;
  const successfulUploads = uploadResults.filter(r => r.success).length;
  const failedUploads = uploadResults.filter(r => !r.success).length;

  // Check if upload should be disabled
  const hasValidationErrors = validationErrors.length > 0;
  const hasExistingDataConflict = existingRecords.length > 0 && !overwriteEnabled;
  const uploadDisabled = hasValidationErrors || hasExistingDataConflict || validFiles === 0;

  // Count total expressions across all files
  const totalExpressions = Array.from(fileData.values()).reduce(
    (sum, data) => sum + data.expressions.length,
    0
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Functions />
        <Typography variant="h5">
          Upload Math Expressions
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Upload JSON files containing mathematical expressions extracted from lesson PDFs.
        Files must follow the mathtastic expression format with contentExpression and decomposed fields.
      </Typography>

      <Grid container spacing={3}>
        {/* File Upload Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              File Upload
            </Typography>

            {!selectedProduct || !selectedExperiment ? (
              <Alert severity="warning">
                Please select a product and experiment from the main page before uploading.
              </Alert>
            ) : (
              <>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  sx={{ mr: 2 }}
                >
                  Select JSON Files
                  <input
                    type="file"
                    accept=".json"
                    multiple
                    hidden
                    onChange={handleFilesUpload}
                  />
                </Button>

                {files.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="success" icon={<CheckCircle />}>
                      {files.length} file(s) uploaded
                    </Alert>
                  </Box>
                )}
              </>
            )}

            {isProcessing && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Processing files...
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* File Processing Results */}
        {(validFiles > 0 || invalidFiles > 0) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                File Processing Results
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {validFiles}
                      </Typography>
                      <Typography variant="body2">
                        Valid Files
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">
                        {invalidFiles}
                      </Typography>
                      <Typography variant="body2">
                        Invalid Files
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">
                        {totalExpressions}
                      </Typography>
                      <Typography variant="body2">
                        Total Expressions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="error">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2">
                        File Validation Errors ({validationErrors.length})
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedErrors(!expandedErrors)}
                      >
                        {expandedErrors ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                    <Collapse in={expandedErrors}>
                      <List dense>
                        {validationErrors.map((errorGroup, index) => (
                          <div key={index}>
                            <ListItem>
                              <ListItemIcon>
                                <Error color="error" />
                              </ListItemIcon>
                              <ListItemText
                                primary={errorGroup.file}
                                secondary={errorGroup.errors.join(', ')}
                              />
                            </ListItem>
                            {index < validationErrors.length - 1 && <Divider />}
                          </div>
                        ))}
                      </List>
                    </Collapse>
                  </Alert>
                </Box>
              )}

              {/* Existing Data Conflict */}
              {existingRecords.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity={overwriteEnabled ? "info" : "warning"}>
                    <Typography variant="subtitle2">
                      {existingRecords.length} record(s) already have math expression data
                    </Typography>
                    {existingRecords.slice(0, 5).map(id => (
                      <Typography variant="body2" key={id}>- {id}</Typography>
                    ))}
                    {existingRecords.length > 5 && (
                      <Typography variant="body2">
                        ... and {existingRecords.length - 5} more
                      </Typography>
                    )}
                  </Alert>
                </Box>
              )}

              {/* Upload Controls */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={overwriteEnabled}
                      onChange={(e) => setOverwriteEnabled(e.target.checked)}
                    />
                  }
                  label="Overwrite existing data"
                />

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Save />}
                  onClick={uploadMathExpressions}
                  disabled={uploadDisabled || isUploading}
                  sx={{ minWidth: 200 }}
                >
                  {isUploading ? 'Uploading...' : `Upload ${validFiles} Files`}
                </Button>
              </Box>

              {uploadDisabled && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning">
                    {hasValidationErrors && 'Fix validation errors before uploading'}
                    {hasExistingDataConflict && 'Enable "Overwrite existing data" to update records with existing data'}
                    {validFiles === 0 && 'No valid files to upload'}
                  </Alert>
                </Box>
              )}

              {isUploading && <LinearProgress sx={{ mt: 2 }} />}
            </Paper>
          </Grid>
        )}

        {/* Upload Results */}
        {uploadResults.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Upload Results
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">
                        {successfulUploads}
                      </Typography>
                      <Typography variant="body2">
                        Successful
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">
                        {failedUploads}
                      </Typography>
                      <Typography variant="body2">
                        Failed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {uploadResults.map((result, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      icon={result.success ? <CheckCircle /> : <Error />}
                      label={result.success ? 'Success' : 'Failed'}
                      color={result.success ? 'success' : 'error'}
                      size="small"
                    />
                    <Typography variant="body2">
                      <strong>{result.id}:</strong> {result.message}
                    </Typography>
                  </Box>

                  {/* Detailed diagnostics for successful uploads */}
                  {result.success && result.details?._diagnostics && (
                    <Box sx={{ ml: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                      <Typography variant="caption" sx={{ color: 'black', fontWeight: 'bold' }} gutterBottom>
                        Upload Details:
                      </Typography>
                      <Grid container spacing={1} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                          <Typography variant="caption" display="block" sx={{ color: 'black', fontWeight: 'bold' }}>
                            S3 Files Stored:
                          </Typography>
                          {Object.entries(result.details._diagnostics.s3Fields).map(([field, info]: [string, any]) => (
                            info.present && (
                              <Typography key={field} variant="caption" display="block" sx={{ ml: 1, color: 'black' }}>
                                - {field.replace(/_/g, ' ')}: {info.count ?
                                  `${info.count} expressions` :
                                  'stored'}
                              </Typography>
                            )
                          ))}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" display="block" sx={{ color: 'black', fontWeight: 'bold' }}>
                            Fields Processed:
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ ml: 1, color: 'black' }}>
                            {result.details._diagnostics.fieldsReceived.join(', ')}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'black' }}>
                        Updated: {new Date(result.details._diagnostics.timestamp).toLocaleTimeString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}

              {successfulUploads > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Successfully uploaded math expressions for {successfulUploads} lesson(s)!
                </Alert>
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
